import classNames from 'classnames'
import { PriceSimulationSectionProps } from './types'
import styles from '../RegistrationSideCard.module.scss'
import { useTranslation } from 'next-i18next/pages'
import { getProductToSelect, getProposition } from 'utils/price'
import PriceDisplay from 'features/simulation/components/proposition-card/price-display/PriceDisplay'
import { InputGroupKeys, PersonalDataFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { useStoreSelector } from 'hooks/store'
import { useRouter } from 'next/router'
import { getProductName } from 'utils/products'
import SimulationRegistrationSideCardLayout from 'components/simulation-registration-side-card-layout/SimulationRegistrationSideCardLayout'
import { DEFAULT_PRODUCT, PRODUCT_CONFIG_OPTIONS, PRODUCTS, PRODUCTS_CONFIG, SIMULATION_TYPES } from '@boltenergy-be/shared'
import { checkEnforcePlenty } from 'features/simulation/utils'

const PriceSimulationSection = ({ setShowPrice, setShowEditInstalmentDrawer }: PriceSimulationSectionProps) => {
  // Redux store
  const {
    flows: { simulation },
    inputs,
    propositions
  } = useStoreSelector((store) => store.customer)
  const { availableProducts } = useStoreSelector((store) => store.marketing)

  // Router
  const { locale } = useRouter()

  // I18n
  const { t } = useTranslation(['simulationCard', 'common'])

  // Constants
  const { region } = inputs[InputGroupKeys.SIMULATION]
  const enforcePlenty = checkEnforcePlenty({ region, availableProducts })
  const isExpressRegistration = inputs.simulation.chosenSimulationType === SIMULATION_TYPES.express
  const proposition = getProposition()
  const selectedProduct = getProductToSelect()
  const counterpartProduct = PRODUCTS_CONFIG[selectedProduct]?.[PRODUCT_CONFIG_OPTIONS.counterpart]
  const counterpartProposition = propositions?.showPlentyPropositions ? getProposition(counterpartProduct) : undefined
  const { forcedAmount } = inputs[InputGroupKeys.SIMULATION]

  /**
   * Returns the price explanation text
   *
   * @returns {string}
   */
  const getPriceExplanationText = (): string => {
    if (inputs[InputGroupKeys.SIMULATION][SimulationFieldKeys.FORCED_AMOUNT]) {
      return t(
        'forcedAmount.description',
        'Je hebt dit bedrag zelf gekozen. Het is niet gebaseerd op een geschat verbruik of de verwachte marktprijzen voor energie.'
      )
    }

    if (isExpressRegistration) {
      return t(
        inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.IS_COMPANY] ? 'expressEstimationCompany' : 'expressEstimation',
        'Het bedrag voor je maandelijks voorschot is een inschatting op basis van het gemiddeld energieverbruik van een Belgisch huishouden en de toekomstige marktprijzen voor energie.'
      )
    }

    if (propositions.selectedProposition === PRODUCTS.fix) {
      return t(
        'simulationEstimationFix',
        'Het bedrag voor je voorschot is een inschatting op basis van een geschat verbruik en een vaste prijs voor energie.'
      )
    }

    return t(
      'simulationEstimation',
      'Het bedrag voor je voorschot is een inschatting op basis van een geschat verbruik en de verwachte marktprijzen voor energie.'
    )
  }

  return (
    <section className={classNames(styles.section, styles.simulation)}>
      <div className={styles['section-header']}>
        <span className={styles['section-title']}>
          {getProductName(propositions?.selectedProposition || availableProducts?.[region]?.[0] || DEFAULT_PRODUCT, locale)}
        </span>
        {!forcedAmount && (
          <SimulationRegistrationSideCardLayout.Action onClick={() => setShowPrice(true)} variant="primary" size="medium">
            {t('viewDetails', 'Bekijk in detail')}
          </SimulationRegistrationSideCardLayout.Action>
        )}
      </div>

      <PriceDisplay
        label={t('perMonth', '/maand')}
        isLoading={simulation.isLoading}
        counterpartProposition={!enforcePlenty ? counterpartProposition : undefined}
        {...{ forcedAmount, proposition }}
      />

      <p className={styles['price-explanation']}>{getPriceExplanationText()}</p>

      <div className={styles['simulation-actions']}>
        <div className={styles['simulation-action']}>
          <SimulationRegistrationSideCardLayout.Action onClick={() => setShowEditInstalmentDrawer(true)}>
            {t('editInstalment', 'Wijzig voorschot')}
          </SimulationRegistrationSideCardLayout.Action>
        </div>
      </div>
    </section>
  )
}

export default PriceSimulationSection
